import { Box, Grid, GridItem } from "@chakra-ui/react";
import { useEffect, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "../../../../common/layout/Layout";
import { EventHeader } from "./layout/EventHeader";
import { MobileStepDrawer } from "./layout/MobileStepDrawer";
import { PublicEventNavigation } from "./layout/PublicEventNavigation";
import { AwardCriteriaStep } from "./steps/AwardCriteriaStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { InfoStep } from "./steps/InfoStep";
import { ProcedureStep } from "./steps/ProcedureStep";
import { QualificationStep } from "./steps/QualificationStep";
import { SummaryStep } from "./steps/SummaryStep";
import {
  bffApi,
  useGetSourcingEventReadinessQuery,
  useUpdateSourcingEventMutation,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../autogen/bff-api";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { WIZARD_STEPS, stepNeighbors, useStepLabel, type PublicEventStep } from "./wizardSteps";
import { CONTENT_WIDTH } from "../../../../config";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEntityState,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { saveWithVersionConflictRetry } from "./saveWithVersionConflictRetry";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  refetch: () => void;
}

export const EditPublicSourcingEventForm = ({ eventId, event, refetch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const { triggerSoft } = useApiError();
  const [updateEvent] = useUpdateSourcingEventMutation();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const { currentStep, navigateToStep } = useStepNavigation(eventId);
  const stepLabel = useStepLabel();

  // Subscribed at the orchestrator level so the readiness cache survives
  // step navigation; sections re-subscribe via useReadinessByField and RTK
  // Query dedupes. Surface fetch failures via the soft toast — without this
  // the badges silently disappear and SummaryStep's Publish gate engages
  // with no user-visible reason.
  const { error: readinessError } = useGetSourcingEventReadinessQuery({ eventId });
  useEffect(() => {
    if (readinessError) triggerSoft(readinessError);
  }, [readinessError, triggerSoft]);

  const config = useMemo(
    () => ({
      initialState: event,
      save: async (fields: Partial<SourcingEventV2PatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateEvent({
              eventId,
              sourcingEventV2PatchDto: retryFields as SourcingEventV2PatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getSourcingEvent.initiate(
                { eventId },
                { forceRefetch: true },
              ),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [dispatch, event, eventId, updateEvent, editorErrorConfig],
  );

  useCreateEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId, config);

  const optimisticEvent = useEntityState<SourcingEventV2Dto>(eventId) ?? event;

  if (!currentStep) return <div>{t("Loading")}</div>;

  const goTo = (step: PublicEventStep | undefined): (() => void) | undefined =>
    step ? () => navigateToStep(step) : undefined;
  const labelFor = (step: PublicEventStep | undefined): string | undefined =>
    step ? stepLabel(step) : undefined;
  const navPropsFor = (step: PublicEventStep) => {
    const { previous, next } = stepNeighbors(step);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: labelFor(next) };
  };

  const stepRenderers: Record<PublicEventStep, () => ReactNode> = {
    info: () => {
      const { nextStep, nextStepLabel } = navPropsFor("info");
      return <InfoStep eventId={eventId} nextStep={nextStep} nextStepLabel={nextStepLabel} />;
    },
    procedure: () => <ProcedureStep eventId={eventId} {...navPropsFor("procedure")} />,
    qualification: () => (
      <QualificationStep
        eventId={eventId}
        isActive={currentStep === "qualification"}
        {...navPropsFor("qualification")}
      />
    ),
    awardCriteria: () => (
      <AwardCriteriaStep
        eventId={eventId}
        isActive={currentStep === "awardCriteria"}
        {...navPropsFor("awardCriteria")}
      />
    ),
    documents: () => (
      <DocumentsStep
        eventId={eventId}
        event={event}
        refetch={refetch}
        isActive={currentStep === "documents"}
        {...navPropsFor("documents")}
      />
    ),
    summary: () => (
      <SummaryStep
        eventId={eventId}
        organization={selectedOrg}
        currentEditor={me}
        responsibleBuyer={selectedOrg.owner}
        previousStep={navPropsFor("summary").previousStep}
        navigateToStep={navigateToStep}
      />
    ),
  };

  return (
    <Layout>
      <MobileStepDrawer currentStep={currentStep} navigateToStep={navigateToStep} />
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows="auto minmax(min-content, auto)"
        templateAreas={`"contentLeft content contentRight"`}
        width="full"
        maxWidth="full"
        overflowX="hidden"
      >
        <GridItem area="content" maxWidth="full" width="full">
          <Grid
            gridTemplateColumns={{ base: "1fr", md: "260px 1fr" }}
            gridTemplateRows="70px minmax(min-content, auto)"
            templateAreas={{
              base: `"contentNav" "content"`,
              md: `"menuNav contentNav" "menu content"`,
            }}
            columnGap={{ base: 0, md: 8 }}
            width="full"
            maxWidth="full"
            marginTop="9px"
            px="2"
          >
            <GridItem area="menuNav" alignItems="end" borderBottom="1px solid" borderColor="smBorder" width="full" maxWidth="full" />
            <GridItem area="contentNav" alignItems="end" borderBottom="1px solid" borderColor="smBorder" overflow="hidden">
              <EventHeader title={optimisticEvent.eventData.title} modifiedAt={optimisticEvent.modifiedAt} />
            </GridItem>
            <GridItem area="menu" display={{ base: "none", md: "block" }}>
              <Box pt="10">
                <PublicEventNavigation currentStep={currentStep} navigateToStep={navigateToStep} />
              </Box>
            </GridItem>
            <GridItem area="content" maxWidth="full" overflowX="hidden">
              <Box maxWidth="full">
                {WIZARD_STEPS.map(({ key }) => (
                  <Box key={key} display={currentStep === key ? undefined : "none"}>
                    {stepRenderers[key]()}
                  </Box>
                ))}
              </Box>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Layout>
  );
};
